import { useEffect, useState } from "react"
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom"
import {
  createBridge,
  createAppClient,
  decodeAppShellToken,
  type AppBridge,
  type AppClient,
  type AppContext,
} from "@koortex/apps-sdk"
import { readRoleFromToken } from "./lib/shell-role"
import { CyclesList } from "./pages/CyclesList"
import { OpenCycles } from "./pages/OpenCycles"
import { CycleDetail } from "./pages/CycleDetail"
import { RadarPage } from "./pages/RadarPage"
import { SolvedPage } from "./pages/SolvedPage"
import { OkrPage } from "./pages/OkrPage"
import { cn } from "./lib/utils"

export interface AppShell {
  context: AppContext
  client: AppClient
  bridge: AppBridge
  /**
   * Role do utilizador, lido do token assinado pelo host. `null` quando a
   * claim não vem no token — nesse caso mostra-se tudo e deixa-se o host
   * recusar. Esconder um ecrã a um administrador é pior falha do que mostrar
   * um separador que devolve 403 com uma mensagem clara.
   */
  role: number | null
}

/**
 * Billing é de administradores. O gate real está no `min_role` de cada action
 * (o host recusa 403) — isto só evita mostrar um ecrã que não vai funcionar;
 * esconder o separador nunca foi a defesa. Tem de andar a par do `min_role`
 * do filho `billing` e das actions no manifest: se divergirem, ou o separador
 * aparece e rebenta, ou desaparece a quem tem direito a ele.
 */
const BILLING_MIN_ROLE = 10

type ShellState =
  | { kind: "loading" }
  | { kind: "ready"; shell: AppShell }
  | { kind: "no-token" }
  | { kind: "bad-token"; error: string }

function useAppShellState(): ShellState {
  const [state, setState] = useState<ShellState>({ kind: "loading" })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")
    if (!token) {
      setState({ kind: "no-token" })
      return
    }
    let context: AppContext
    try {
      context = decodeAppShellToken(token)
    } catch (err) {
      setState({ kind: "bad-token", error: err instanceof Error ? err.message : String(err) })
      return
    }
    const client = createAppClient(token, context)
    const bridge = createBridge({
      allowedCapabilities: ["toast", "navigate.crm", "open.preview", "request.action"],
    })
    // A claim `role` não vem no AppContext do SDK — ver lib/shell-role.ts.
    const role = readRoleFromToken(token)
    setState({ kind: "ready", shell: { context, client, bridge, role } })
  }, [])

  return state
}

function canSeeBilling(role: number | null): boolean {
  return role === null || role >= BILLING_MIN_ROLE
}

function Tabs({ role }: { role: number | null }) {
  const { pathname } = useLocation()
  const tabs = [
    { to: "/okr", label: "Objetivos", hint: "para onde vamos?" },
    { to: "/radar", label: "Project Radar", hint: "onde focar agora?" },
    { to: "/solved", label: "O Que Resolvemos", hint: "o que ficou melhor?" },
    ...(canSeeBilling(role)
      ? [{ to: "/billing", label: "Billing", hint: "ciclos por faturar" }]
      : []),
  ]
  return (
    <nav className="flex flex-wrap items-center gap-6 border-b px-6">
      {tabs.map((tab) => {
        const active = pathname.startsWith(tab.to)
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={cn(
              "flex items-baseline gap-2 border-b-2 py-3 text-sm font-semibold transition-colors",
              active
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
            <span className="hidden text-xs font-normal text-muted-foreground md:inline">
              — {tab.hint}
            </span>
          </NavLink>
        )
      })}
    </nav>
  )
}

/** Ecrã inicial: o que o host pediu na claim `path`, ou o Radar. */
function entryRoute(shell: AppShell): string {
  const requested = (shell.context.path ?? "").replace(/^\/+/, "").trim()
  if (!requested || requested === "index.html") return "/radar"
  if (requested.startsWith("billing") || requested.startsWith("cycle")) {
    return canSeeBilling(shell.role) ? `/${requested}` : "/radar"
  }
  return `/${requested}`
}

/**
 * O Billing tem dois momentos distintos e o ecrã deve dizê-lo: primeiro
 * decide-se o que fechar, depois revê-se e factura-se o que fechou.
 */
function BillingSubTabs({ visible }: { visible: boolean }) {
  const { pathname } = useLocation()
  if (!visible || !pathname.startsWith("/billing")) return null
  const items = [
    { to: "/billing", label: "Por fechar", hint: "ciclos com horas por congelar" },
    { to: "/billing/invoicing", label: "Por faturar", hint: "ciclos fechados" },
  ]
  return (
    <nav className="flex flex-wrap items-center gap-5 border-b bg-muted/30 px-6">
      {items.map((item) => {
        const active = pathname === item.to
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end
            className={cn(
              "flex items-baseline gap-2 border-b-2 py-2 text-sm transition-colors",
              active
                ? "border-primary font-semibold text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
            <span className="hidden text-xs font-normal text-muted-foreground md:inline">
              — {item.hint}
            </span>
          </NavLink>
        )
      })}
    </nav>
  )
}

export function App() {
  const state = useAppShellState()

  if (state.kind === "loading") {
    return <div className="p-6 text-sm text-muted-foreground">A inicializar app shell…</div>
  }
  if (state.kind === "no-token") {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Zenn Ops — standalone dev mode</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sem <code className="font-mono">?token=...</code> no URL. Abre via o host CRM em{" "}
          <code className="font-mono">/pt/apps/zenn-billing</code>.
        </p>
      </div>
    )
  }
  if (state.kind === "bad-token") {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-destructive">Token inválido</h1>
        <pre className="mt-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs">
          {state.error}
        </pre>
      </div>
    )
  }

  const { shell } = state
  const canBill = canSeeBilling(shell.role)

  return (
    <div className="min-h-screen">
      <Tabs role={shell.role} />
      <BillingSubTabs visible={canBill} />
      <Routes>
        <Route path="/okr" element={<OkrPage shell={shell} />} />
        <Route path="/radar" element={<RadarPage shell={shell} />} />
        <Route path="/solved" element={<SolvedPage shell={shell} />} />
        {/* O fluxo mensal começa no que falta fechar, não no que já fechou. */}
        <Route
          path="/billing"
          element={canBill ? <OpenCycles shell={shell} /> : <Navigate to="/radar" replace />}
        />
        <Route
          path="/billing/invoicing"
          element={canBill ? <CyclesList shell={shell} /> : <Navigate to="/radar" replace />}
        />
        <Route
          path="/cycle/:contractId/:milestoneId"
          element={canBill ? <CycleDetail shell={shell} /> : <Navigate to="/radar" replace />}
        />
        {/* O host abre sempre o iframe em <base>/index.html?token=… e diz qual
            o ecrã na claim `path` do token — sem isto, clicar em "Billing" no
            menu do CRM caía no Radar. */}
        <Route path="*" element={<Navigate to={entryRoute(shell)} replace />} />
      </Routes>
    </div>
  )
}
